/** Paramètres → Formats : devise, séparateur, date, 24 h, exercice. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { useCompany } from "../../company/CompanyProvider";
import { fiscalYearLabel, formatCents, formatDay, formatTimePreview, MONTHS_FR } from "../../formats/display";
import { formatsFromCompany, type CompanyFormats } from "../../formats/types";
import { isCompanyOwner } from "./owner";

const SAMPLE_CENTS = 123456;
const SAMPLE_DAY = "2026-09-15";

export function FormatsTab() {
  const { company, updateFormats } = useCompany();
  const [draft, setDraft] = useState<CompanyFormats | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(formatsFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const companyId = company.id;
  const owner = isCompanyOwner(company);

  function set<K extends keyof CompanyFormats>(key: K, value: CompanyFormats[K]) {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateFormats(companyId, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Formats</h2>
        <p>Devise, montants et dates — pour les écrans et les pièces.</p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre les formats.</p>
        ) : null}
      </header>
      <div className="fx-fmt">
        <div className="fx-fmt-row">
          <span>Devise</span>
          <label className="fx-field flex flex-col gap-1 text-xs">
            Euro
            <select className="rounded-md border px-2.5 py-2 text-sm" value="EUR" disabled>
              <option value="EUR">Euro (EUR)</option>
            </select>
          </label>
        </div>
        <div className="fx-fmt-row">
          <span>Affichage</span>
          <div className="fx-fmt-picks" role="radiogroup" aria-label="Affichage de la devise">
            <Pick
              checked={draft.currency_display === "symbol"}
              label="Symbole"
              sample={formatCents(SAMPLE_CENTS, { ...draft, currency_display: "symbol" })}
              onPick={() => set("currency_display", "symbol")}
            />
            <Pick
              checked={draft.currency_display === "code"}
              label="Code"
              sample={formatCents(SAMPLE_CENTS, { ...draft, currency_display: "code" })}
              onPick={() => set("currency_display", "code")}
            />
          </div>
        </div>
        <div className="fx-fmt-row">
          <span>Séparateur</span>
          <CheckField
            label="Virgule décimale (1 000,00) — sinon point (1,000.00)"
            checked={draft.decimal_comma}
            onChange={(next) => set("decimal_comma", next)}
          />
        </div>
        <div className="fx-fmt-row">
          <span>Date</span>
          <p className="fx-co-form__hint m-0">
            {formatDay(SAMPLE_DAY, draft)} — jour / mois / année.
          </p>
        </div>
        <div className="fx-fmt-row">
          <span>Heure</span>
          <CheckField label="24 heures (14:30)" checked={draft.time_24h} onChange={(next) => set("time_24h", next)} />
        </div>
        <div className="fx-fmt-row">
          <span>Premier mois</span>
          <label className="fx-field flex flex-col gap-1 text-xs">
            Début d’exercice
            <select
              className="rounded-md border px-2.5 py-2 text-sm"
              value={draft.fiscal_year_start}
              onChange={(e) => set("fiscal_year_start", Number(e.target.value))}
            >
              {MONTHS_FR.map((label, i) => (
                <option key={label} value={i + 1}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <aside className="fx-fmt-preview" aria-label="Aperçu">
          <strong>Aperçu</strong>
          <p>{formatCents(SAMPLE_CENTS, draft)}</p>
          <p>{formatDay(SAMPLE_DAY, draft)}</p>
          <p>{formatTimePreview(draft)}</p>
          <p>Exercice : {fiscalYearLabel(draft)}</p>
        </aside>
      </div>
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

function Pick({
  checked,
  label,
  sample,
  onPick,
}: {
  checked: boolean;
  label: string;
  sample: string;
  onPick: () => void;
}) {
  return (
    <button type="button" role="radio" aria-checked={checked} className={checked ? "is-on" : ""} onClick={onPick}>
      <strong>{label}</strong>
      <em>{sample}</em>
    </button>
  );
}
